/**
 * Project list card (§7g + v2.5 UX overhaul).
 *
 * Clickable card linking to the project view. Shows account name,
 * company URL (external link), and relative updated time.
 */

import { Link } from "react-router-dom";

const RELATIVE_UNITS = [
  { limit: 60, divisor: 1, unit: "second" },
  { limit: 3600, divisor: 60, unit: "minute" },
  { limit: 86400, divisor: 3600, unit: "hour" },
  { limit: 2592000, divisor: 86400, unit: "day" },
  { limit: 31536000, divisor: 2592000, unit: "month" },
  { limit: Infinity, divisor: 31536000, unit: "year" },
];

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

function formatRelative(iso) {
  if (!iso) return null;
  const diffSec = (Date.now() - new Date(iso).getTime()) / 1000;
  const abs = Math.abs(diffSec);
  const { divisor, unit } = RELATIVE_UNITS.find((u) => abs < u.limit);
  return rtf.format(-Math.round(diffSec / divisor), unit);
}

function hostnameOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

const ROLE_LABELS = { owner: "Owner", editor: "Editor", viewer: "Viewer" };

export default function ProjectCard({ project, showRole = false }) {
  const { id, name, companyUrl, updatedAt, status, role } = project;
  const updated = formatRelative(updatedAt);
  const archived = status === "archived";

  return (
    <Link
      to={`/projects/${id}`}
      className={`project-card${archived ? " project-card--archived" : ""}`}
    >
      <div className="project-card-title">{name}</div>
      {companyUrl && (
        <a
          className="project-card-url"
          href={companyUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {hostnameOf(companyUrl)}
        </a>
      )}
      {updated && <div className="project-card-meta">Updated {updated}</div>}
      {showRole && role && role !== "owner" && (
        <span className={`member-role-badge member-role-${role}`}>
          {ROLE_LABELS[role] || role}
        </span>
      )}
    </Link>
  );
}
